import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';

// ── Utility ─────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '—';
  const s = (typeof d === 'string' ? d : '').slice(0, 10);
  const [y, m, day] = s.split('-');
  return day && m && y ? day + '/' + m + '/' + y : '—';
};
const fmtEur = v => Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';
const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const STATUS_LABEL = {
  planned: 'Pianificata', in_progress: 'In corso', at_risk: 'A rischio',
  done: 'Completata', blocked: 'Bloccata',
};
const BADGE = {
  done:        { backgroundColor: '#E8F5EF', color: '#0F6E56' },
  in_progress: { backgroundColor: '#EEF3FF', color: '#2D4BA0' },
  at_risk:     { backgroundColor: '#FEF6E7', color: '#8B5E00' },
  planned:     { backgroundColor: '#F5F5F5', color: '#555' },
  blocked:     { backgroundColor: '#FDEEEE', color: '#9B1C1C' },
};
const TASK_LABEL = { todo: 'Da fare', in_progress: 'In corso', done: 'Completata', skipped: 'Saltata' };
const FONTE_LABEL = { ordine_fornitore: 'Ordine fornitore', report_giornaliero: 'Report giornaliero' };
const LABELS_MAP = { prima_dei_lavori: 'Prima dei lavori', in_corso: 'In corso', fine_lavori: 'Fine lavori', problema: 'Problema', materiale: 'Materiale', altro: 'Altro' };

// ── Styles ──────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  page: { paddingTop: 71, paddingBottom: 71, paddingLeft: 40, paddingRight: 40, fontSize: 8, fontFamily: 'Helvetica', color: '#1a1a2e' },
  headerBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: '#2EA084' },
  headerWrap: { position: 'absolute', top: 12, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 8, borderBottom: '1pt solid #e9ecef' },
  headerLeft: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#2EA084' },
  headerRight: { fontSize: 8, color: '#868e96', textAlign: 'right' },
  footer: { position: 'absolute', bottom: 24, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7, color: '#868e96', borderTop: '0.5pt solid #e9ecef', paddingTop: 6 },
  sh: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 4, paddingBottom: 4, fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#0A1628', backgroundColor: '#fff', marginBottom: 6, marginTop: 14 },
  kpiRow: { flexDirection: 'row', marginBottom: 8 },
  kpiBox: { border: '1pt solid #e9ecef', borderRadius: 4, padding: '8 10', flex: 1, margin: '0 4' },
  kpiL: { fontSize: 7, color: '#868e96', textTransform: 'uppercase', marginBottom: 2 },
  kpiV: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  th: { flexDirection: 'row', backgroundColor: '#f8f9fa', borderBottom: '1pt solid #e9ecef', padding: '4 6', fontFamily: 'Helvetica-Bold', fontSize: 7 },
  tr: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8 },
  trA: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8, backgroundColor: '#f8f9fa' },
  trT: { flexDirection: 'row', padding: '5 6', fontSize: 8, fontFamily: 'Helvetica-Bold', backgroundColor: '#F5F5F5', color: '#0A1628', borderTop: '1pt solid #2EA084' },
  gh: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 3, paddingBottom: 3, fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#0A1628', marginTop: 8, marginBottom: 2 },
  badge: { fontSize: 7, padding: '1 4', borderRadius: 2 },
  twoCol: { flexDirection: 'row', gap: 20 },
  col: { flex: 1 },
  row: { flexDirection: 'row', justifyContent: 'space-between', padding: '3 0', borderBottom: '0.5pt solid #e9ecef' },
  rowL: { fontSize: 8, color: '#868e96' },
  rowV: { fontSize: 8, color: '#1a1a2e' },
  // Copertina
  cover: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  coverTitle: { fontSize: 28, fontFamily: 'Helvetica-Bold', color: '#0A1628', marginBottom: 8 },
  coverSub: { fontSize: 14, color: '#868e96', marginBottom: 4 },
  coverBadge: { fontSize: 11, padding: '4 12', borderRadius: 4, marginTop: 12 },
  // Foto
  imgGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  imgCell: { width: 80, height: 80, borderRadius: 4, overflow: 'hidden', border: '0.5pt solid #e9ecef' },
  imgLabel: { fontSize: 6, color: '#868e96', marginTop: 2, width: 80 },
});

const Header = ({ title, azienda }) => (
  <>
    <View style={s.headerBar} fixed />
    <View style={s.headerWrap} fixed>
      <Text style={s.headerLeft}>{title}</Text>
      <View style={s.headerRight}>
        <Text>{azienda?.nome || ''}</Text>
        <Text>Generato il {new Date().toLocaleDateString('it-IT')}</Text>
      </View>
    </View>
  </>
);
const Footer = ({ label, cantiere }) => (
  <View style={s.footer} fixed>
    <Text>mAIstro — {label}</Text>
    <Text>{cantiere?.titolo || ''}</Text>
    <Text render={({ pageNumber, totalPages }) => 'Pagina ' + pageNumber + ' di ' + totalPages} />
  </View>
);
const R = ({ label, value }) => value ? (
  <View style={s.row}><Text style={s.rowL}>{label}</Text><Text style={s.rowV}>{value}</Text></View>
) : null;

// ── Componente ──────────────────────────────────────────────────────────────

export default function CantierePDFCompleto({ data, piano, reports, documenti, spese, foto }) {
  const cant = data?.cantiere || {};
  const az = data?.azienda || {};
  const b = data?.budget || {};
  const p = data?.piano_lavori || {};
  const milestone = data?.milestone || [];
  const c = data?.contatori || {};
  const ur = data?.ultimo_report;

  const pianoFull = piano || {};
  const fasi = pianoFull.fasi || p.fasi || [];
  const milestoneF = pianoFull.milestone || milestone;
  const reps = reports || [];
  const docs = documenti || [];
  const costiList = spese || [];
  // Esclude i video (non renderizzabili in PDF). Video WhatsApp: metadata.media_kind === 'video' (cfr. FotoTab.jsx)
  const fotoList = (foto || []).filter(f =>
    f.metadata?.media_kind !== 'video' && !(f.metadata?.mime_type || '').includes('video')
  ).slice(0, 30);

  const totSpese = costiList.reduce((s, e) => s + Number(e.importo || 0), 0);

  return (
    <Document>
      {/* ── 1. COPERTINA ──────────────────────────────────── */}
      <Page size="A4" style={s.page}>
        <View style={s.headerBar} fixed />
        <View style={s.cover}>
          <Text style={{ fontSize: 11, color: '#2EA084', fontFamily: 'Helvetica-Bold', marginBottom: 20 }}>mAIstro</Text>
          <Text style={s.coverTitle}>{cant.titolo || 'Cantiere'}</Text>
          <Text style={s.coverSub}>{az.nome || ''}</Text>
          <Text style={s.coverSub}>{cant.indirizzo || ''}</Text>
          <Text style={s.coverSub}>Cliente: {cant.cliente_nome || '—'}</Text>
          <Text style={[s.coverBadge, BADGE[cant.status] || BADGE.planned]}>
            {cant.status || '—'}
          </Text>
          <Text style={{ fontSize: 9, color: '#868e96', marginTop: 30 }}>
            Documento generato il {new Date().toLocaleDateString('it-IT')}
          </Text>
        </View>
        <Footer label="Dossier Cantiere" cantiere={cant} />
      </Page>

      {/* ── 2. PANORAMICA ─────────────────────────────────── */}
      <Page size="A4" style={s.page}>
        <Header title={'Panoramica — ' + (cant.titolo || '')} azienda={az} />

        <Text style={s.sh}>Anagrafica</Text>
        <View style={s.twoCol}>
          <View style={s.col}>
            <R label="Titolo" value={cant.titolo} />
            <R label="Indirizzo" value={cant.indirizzo} />
            <R label="Stato" value={cant.status} />
            <R label="Inizio" value={fmtDate(cant.data_inizio)} />
            <R label="Fine stimata" value={fmtDate(cant.data_fine_stimata)} />
          </View>
          <View style={s.col}>
            <R label="Cliente" value={cant.cliente_nome} />
            <R label="Documenti" value={String(c.documenti || 0)} />
            <R label="Fatture aperte" value={String(c.fatture_aperte || 0)} />
          </View>
        </View>

        <Text style={s.sh}>Numeri chiave</Text>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}><Text style={s.kpiL}>Avanzamento</Text><Text style={[s.kpiV, { color: '#2EA084' }]}>{p.percentuale_avanzamento || 0}%</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiL}>Budget</Text><Text style={s.kpiV}>{fmtEur(b.budget_totale)}</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiL}>Speso</Text><Text style={[s.kpiV, { color: '#E85D5D' }]}>{fmtEur(b.budget_speso)}</Text></View>
        </View>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}><Text style={s.kpiL}>Valore commessa</Text><Text style={s.kpiV}>{fmtEur(b.valore_commessa)}</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiL}>Fatturato</Text><Text style={[s.kpiV, { color: '#2EA084' }]}>{fmtEur(b.fatturato)}</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiL}>Da incassare</Text><Text style={[s.kpiV, { color: '#F5A623' }]}>{fmtEur(b.da_incassare)}</Text></View>
        </View>

        {ur && (
          <>
            <Text style={s.sh}>Ultimo report</Text>
            <R label="Data" value={fmtDate(ur.data)} />
            <R label="Avanzamento SAL" value={ur.avanzamento_sal + '%'} />
            <R label="Problemi aperti" value={String(ur.problemi_aperti)} />
          </>
        )}
        <Footer label="Panoramica" cantiere={cant} />
      </Page>

      {/* ── 3. PIANO LAVORI ───────────────────────────────── */}
      {fasi.length > 0 && (
        <Page size="A4" style={s.page}>
          <Header title={'Piano Lavori — ' + (cant.titolo || '')} azienda={az} />

          <Text style={s.sh}>Fasi ({fasi.length})</Text>
          <View style={s.th}>
            <Text style={{ flex: 3 }}>Fase</Text><Text style={{ flex: 1.5 }}>Stato</Text>
            <Text style={{ flex: 1.5 }}>Inizio</Text><Text style={{ flex: 1.5 }}>Fine</Text>
            <Text style={{ flex: 0.8, textAlign: 'right' }}>%</Text>
          </View>
          {fasi.map((f, i) => (
            <View key={f.id || i} style={i % 2 === 0 ? s.tr : s.trA}>
              <Text style={{ flex: 3 }}>{f.nome}</Text>
              <View style={{ flex: 1.5 }}><Text style={[s.badge, BADGE[f.status] || BADGE.planned, { alignSelf: 'flex-start' }]}>{STATUS_LABEL[f.status] || f.status}</Text></View>
              <Text style={{ flex: 1.5 }}>{fmtDate(f.data_inizio_pianificata)}</Text>
              <Text style={{ flex: 1.5 }}>{fmtDate(f.data_fine_pianificata)}</Text>
              <Text style={{ flex: 0.8, textAlign: 'right' }}>{f.percentuale_completamento || 0}%</Text>
            </View>
          ))}

          {/* Task per fase */}
          {fasi.filter(f => f.tasks && f.tasks.length > 0).map(f => (
            <View key={'t' + f.id} wrap={false}>
              <Text style={[s.sh, { fontSize: 8 }]}>Task — {f.nome}</Text>
              <View style={s.th}><Text style={{ flex: 3 }}>Task</Text><Text style={{ flex: 1.5 }}>Stato</Text><Text style={{ flex: 1.5 }}>Prevista</Text><Text style={{ flex: 1.5 }}>Completato</Text></View>
              {f.tasks.map((t, ti) => (
                <View key={t.id || ti} style={ti % 2 === 0 ? s.tr : s.trA}>
                  <Text style={{ flex: 3, color: t.status === 'done' ? '#2EA084' : '#1a1a2e' }}>{t.nome}</Text>
                  <Text style={{ flex: 1.5 }}>{TASK_LABEL[t.status] || t.status}</Text>
                  <Text style={{ flex: 1.5 }}>{fmtDate(t.data_prevista)}</Text>
                  <Text style={{ flex: 1.5 }}>{t.completato_il ? fmtDate(t.completato_il) : '—'}</Text>
                </View>
              ))}
            </View>
          ))}

          {milestoneF.length > 0 && (
            <>
              <Text style={s.sh}>Milestone</Text>
              {milestoneF.map((m, i) => (
                <View key={m.id || i} style={i % 2 === 0 ? s.tr : s.trA}>
                  <Text style={{ flex: 3 }}>{m.nome}</Text>
                  <Text style={{ flex: 1.5 }}>{fmtDate(m.data)}</Text>
                  <Text style={{ flex: 1.5, textAlign: 'right' }}>{m.importo_sal ? fmtEur(m.importo_sal) : '—'}</Text>
                  <Text style={{ flex: 1, color: m.raggiunta ? '#2EA084' : '#868e96' }}>{m.raggiunta ? 'Si' : 'No'}</Text>
                </View>
              ))}
            </>
          )}
          <Footer label="Piano Lavori" cantiere={cant} />
        </Page>
      )}

      {/* ── 4. REPORT GIORNALIERI ─────────────────────────── */}
      {reps.length > 0 && (
        <Page size="A4" style={s.page}>
          <Header title={'Report Giornalieri — ' + (cant.titolo || '')} azienda={az} />
          <Text style={s.sh}>Report ({reps.length})</Text>
          {reps.map((r, i) => (
            <View key={r.id || i} wrap={false} style={{ marginBottom: 8, padding: '8 10', border: '0.5pt solid #e9ecef', borderRadius: 4 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>Giorno {r.giorno_numero} — {fmtDate(r.data)}</Text>
                <Text style={{ fontSize: 7, color: '#868e96' }}>{r.meteo || ''} · {r.numero_operai} operai · {r.ore_totali}h</Text>
              </View>
              {r.fase_corrente && <Text style={{ fontSize: 7, color: '#868e96', marginBottom: 2 }}>Fase: {r.fase_corrente}</Text>}
              {r.attivita_svolte && <Text style={{ fontSize: 8, marginBottom: 2 }}>{r.attivita_svolte.slice(0, 300)}</Text>}
              <View style={{ height: 4, borderRadius: 2, backgroundColor: '#e9ecef', marginTop: 4 }}>
                <View style={{ height: 4, borderRadius: 2, backgroundColor: '#2EA084', width: (r.percentuale_avanzamento_globale || 0) + '%' }} />
              </View>
              <Text style={{ fontSize: 7, color: '#868e96', marginTop: 2 }}>{r.percentuale_avanzamento_globale || 0}% completato</Text>
            </View>
          ))}
          <Footer label="Report Giornalieri" cantiere={cant} />
        </Page>
      )}

      {/* ── 5. DOCUMENTI ──────────────────────────────────── */}
      {docs.length > 0 && (
        <Page size="A4" style={s.page}>
          <Header title={'Documenti — ' + (cant.titolo || '')} azienda={az} />
          <Text style={s.sh}>Documenti ({docs.length})</Text>
          <View style={s.th}>
            <Text style={{ flex: 4 }}>Nome file</Text><Text style={{ flex: 1.5 }}>Tipo</Text>
            <Text style={{ flex: 1.5 }}>Data</Text><Text style={{ flex: 1, textAlign: 'right' }}>Dim.</Text>
          </View>
          {docs.map((d, i) => (
            <View key={d.id || i} style={i % 2 === 0 ? s.tr : s.trA}>
              <Text style={{ flex: 4 }}>{d.nome_file || '—'}</Text>
              <Text style={{ flex: 1.5 }}>{d.tipo || '—'}</Text>
              <Text style={{ flex: 1.5 }}>{fmtDate(d.data_upload)}</Text>
              <Text style={{ flex: 1, textAlign: 'right' }}>{fmtSize(d.dimensione_bytes)}</Text>
            </View>
          ))}
          <Footer label="Documenti" cantiere={cant} />
        </Page>
      )}

      {/* ── 6. SPESE ──────────────────────────────────────── */}
      {costiList.length > 0 && (
        <Page size="A4" style={s.page}>
          <Header title={'Spese — ' + (cant.titolo || '')} azienda={az} />
          <Text style={s.sh}>Riepilogo spese</Text>
          <View style={s.kpiRow}>
            <View style={s.kpiBox}><Text style={s.kpiL}>Budget</Text><Text style={s.kpiV}>{fmtEur(b.budget_totale)}</Text></View>
            <View style={s.kpiBox}><Text style={s.kpiL}>Speso</Text><Text style={[s.kpiV, { color: '#E85D5D' }]}>{fmtEur(totSpese)}</Text></View>
            <View style={s.kpiBox}><Text style={s.kpiL}>Residuo</Text><Text style={[s.kpiV, { color: (b.budget_totale || 0) - totSpese >= 0 ? '#2EA084' : '#E85D5D' }]}>{fmtEur((b.budget_totale || 0) - totSpese)}</Text></View>
          </View>
          <Text style={s.sh}>Dettaglio</Text>
          <View style={s.th}>
            <Text style={{ flex: 1.5 }}>Data</Text><Text style={{ flex: 3 }}>Descrizione</Text>
            <Text style={{ flex: 1.5, textAlign: 'right' }}>Importo</Text><Text style={{ flex: 2 }}>Fonte</Text>
          </View>
          {costiList.map((e, i) => (
            <View key={e.id || i} style={i % 2 === 0 ? s.tr : s.trA}>
              <Text style={{ flex: 1.5 }}>{fmtDate(e.data_riferimento || e.created_at)}</Text>
              <Text style={{ flex: 3 }}>{e.contenuto || '—'}</Text>
              <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(e.importo)}</Text>
              <Text style={{ flex: 2 }}>{FONTE_LABEL[e.metadata?.fonte] || 'Manuale'}</Text>
            </View>
          ))}
          <View style={s.trT}>
            <Text style={{ flex: 1.5 }}>Totale</Text><Text style={{ flex: 3 }}>{costiList.length} voci</Text>
            <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(totSpese)}</Text><Text style={{ flex: 2 }} />
          </View>
          <Footer label="Spese" cantiere={cant} />
        </Page>
      )}

      {/* ── 7. FOTO ───────────────────────────────────────── */}
      {fotoList.length > 0 && (
        <Page size="A4" style={s.page}>
          <Header title={'Foto — ' + (cant.titolo || '')} azienda={az} />
          <Text style={s.sh}>Documentazione fotografica ({fotoList.length} foto)</Text>
          {(() => {
            const grouped = {};
            fotoList.forEach(f => {
              const l = f.metadata?.label || 'altro';
              if (!grouped[l]) grouped[l] = [];
              grouped[l].push(f);
            });
            return Object.entries(grouped).map(([label, lista]) => (
              <View key={label}>
                <Text style={s.gh}>{LABELS_MAP[label] || label} ({lista.length})</Text>
                <View style={s.imgGrid}>
                  {lista.map(f => {
                    const src = f.allegato_url || f.allegato;
                    return (
                      <View key={f.id} style={{ marginBottom: 6 }}>
                        {src ? <Image src={src} style={s.imgCell} /> : (
                          <View style={[s.imgCell, { backgroundColor: '#f8f9fa', justifyContent: 'center', alignItems: 'center' }]}>
                            <Text style={{ fontSize: 6, color: '#868e96' }}>N/D</Text>
                          </View>
                        )}
                        <Text style={s.imgLabel}>{fmtDate(f.data_riferimento || f.created_at?.slice(0, 10))}</Text>
                      </View>
                    );
                  })}
                </View>
              </View>
            ));
          })()}
          <Footer label="Documentazione Fotografica" cantiere={cant} />
        </Page>
      )}
    </Document>
  );
}
